import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Sheet, StarIcon, Textarea, cn } from '@openroad/ui';
import { api, unwrap } from '../lib/api';

export interface PendingReview {
  bookingId: string;
  direction: 'PASSENGER_TO_DRIVER' | 'DRIVER_TO_PASSENGER';
  trip: { id: string; origin: string; destination: string; departureAt: string };
  subject: { id: string; displayName: string; initials: string; avatarColour: string };
}

/**
 * Writing a review.
 *
 * The export has no review form — it only draws the "Rate trip" button that
 * opens one — so this is built from the design's own parts: the sheet, the star
 * glyphs the review list uses, and the standard field and CTA.
 * See docs/design-fidelity.md.
 *
 * A review cannot be edited once written, which the copy says plainly before
 * it is submitted rather than after.
 */
export function RateTripSheet({
  pending,
  onClose,
}: {
  pending: PendingReview | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');

  const submit = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/bookings/{id}/review', {
          params: { path: { id: pending!.bookingId } },
          body: { rating, body: body.trim() },
        }),
      ),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['reviews', 'pending'] }),
        // The subject's average moved, and it is printed on their profile and
        // beside every ride they drive.
        queryClient.invalidateQueries({ queryKey: ['trips', 'mine'] }),
        queryClient.invalidateQueries({ queryKey: ['bookings', 'mine'] }),
      ]);
      setRating(0);
      setBody('');
      onClose();
    },
  });

  function send(event: FormEvent) {
    event.preventDefault();
    if (rating === 0 || body.trim() === '' || submit.isPending) return;
    submit.mutate();
  }

  return (
    <Sheet
      open={pending !== null}
      onClose={onClose}
      placement="side"
      label="Rate this trip"
      header={<span className="font-display text-title font-bold">Rate this trip</span>}
    >
      {pending ? (
        <form onSubmit={send} className="flex flex-col gap-5 p-[18px]">
          <div className="flex items-center gap-3">
            <Avatar
              initials={pending.subject.initials}
              colour={pending.subject.avatarColour}
              size={46}
              aria-hidden="true"
            />
            <div>
              <div className="text-base font-bold">{pending.subject.displayName}</div>
              <div className="text-meta text-muted">
                {pending.trip.origin} → {pending.trip.destination}
              </div>
            </div>
          </div>

          <fieldset className="m-0 border-0 p-0">
            <legend className="mb-2 p-0 text-label font-semibold">
              How was it,{' '}
              {pending.direction === 'PASSENGER_TO_DRIVER' ? 'as a driver' : 'as a passenger'}?
            </legend>
            <div className="flex gap-1.5" role="radiogroup" aria-label="Rating out of five">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  role="radio"
                  aria-checked={rating === star}
                  aria-label={`${star} ${star === 1 ? 'star' : 'stars'}`}
                  onClick={() => setRating(star)}
                  className={cn(
                    'flex h-11 w-11 cursor-pointer items-center justify-center rounded-input border bg-surface',
                    star <= rating
                      ? 'border-pohutukawa text-pohutukawa'
                      : 'border-line text-line-firm hover:bg-hover-soft',
                  )}
                >
                  <StarIcon size={22} />
                </button>
              ))}
            </div>
          </fieldset>

          <Textarea
            label="What should other members know?"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={5}
            maxLength={2000}
            placeholder="Punctual, tidy car, good kōrero…"
          />

          <p className="m-0 text-mini text-subtle">
            Reviews are public and cannot be edited once posted.
          </p>

          {submit.isError ? (
            <p className="m-0 text-meta text-danger" role="alert">
              {(submit.error as { message?: string })?.message ??
                'That review did not save. Try again.'}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="cta"
            size="hero"
            block
            disabled={rating === 0 || body.trim() === '' || submit.isPending}
          >
            {submit.isPending ? 'Posting…' : 'Post review'}
          </Button>
        </form>
      ) : null}
    </Sheet>
  );
}
